import { dx, dy, opposite, turnLeft, turnRight, type Direction } from './direction';

/**
 * Wide mowers (cut width 2) cover their "anchor" tile plus the tile beside it. Which side is fixed
 * for a run (chosen at spawn so the mower fits), always relative to the heading: a mower with its
 * second track on the right keeps it on the right through every turn, pivoting around the anchor.
 */
export type Side = 'left' | 'right';
export type CutWidth = 1 | 2;

export interface Pos {
  x: number;
  y: number;
}

export function sideDir(dir: Direction, side: Side): Direction {
  return side === 'left' ? turnLeft(dir) : turnRight(dir);
}

/** Tiles covered by a mower anchored at (x, y) heading `dir`. */
export function footprint(
  x: number,
  y: number,
  dir: Direction,
  width: CutWidth,
  side: Side,
): Pos[] {
  if (width === 1) return [{ x, y }];
  const s = sideDir(dir, side);
  return [
    { x, y },
    { x: x + dx(s), y: y + dy(s) },
  ];
}

/**
 * Tiles that must be clear for a mower at (x, y) to pivot from `from` to `to` in place: the old and
 * new footprints plus the corner tile the body sweeps through. A narrow mower pivots on the spot.
 */
export function pivotTiles(
  x: number,
  y: number,
  from: Direction,
  to: Direction,
  width: CutWidth,
  side: Side,
): Pos[] {
  if (width === 1) return [];
  const a = sideDir(from, side);
  const b = sideDir(to, side);
  const tiles = [...footprint(x, y, from, width, side), ...footprint(x, y, to, width, side)];
  if (b !== opposite(a)) tiles.push({ x: x + dx(a) + dx(b), y: y + dy(a) + dy(b) });
  return tiles;
}

/** The tiles a mower's floating side reels reach: one either side of the anchor. */
export function wingTiles(x: number, y: number, dir: Direction): Pos[] {
  const l = turnLeft(dir);
  const r = turnRight(dir);
  return [
    { x: x + dx(l), y: y + dy(l) },
    { x: x + dx(r), y: y + dy(r) },
  ];
}

/** Picks the side a wide mower's second track sits on at spawn: left if it fits, else right. */
export function chooseSide(
  open: (x: number, y: number) => boolean,
  start: Pos & { dir: Direction },
): Side | null {
  for (const side of ['left', 'right'] as const) {
    if (footprint(start.x, start.y, start.dir, 2, side).every((p) => open(p.x, p.y))) return side;
  }
  return null;
}
